import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { FormattedMessage as FM } from "react-intl";

import { TrashIcon, ChevronDownIcon } from "components/Icons";
import FormField from "componentsv2/form/FormField";
import { cn, colors } from "tools/ui";
import { strTranslate } from "componentsv2/tools/functions";
import { theme } from "theme/theme";

const style = "z-10 py-1 xl:py-2 px-2 w-full h-full bg-white";

/**
 * Field Form Selection.
 *
 * @param {object} props - component props
 * @param {string} props.name - field name
 * @param {string} props.label - field label
 * @param {boolean} props.readOnly - whether field is read-only
 * @param {string} props.model - model name to search for options
 * @param {string} props.placeholder - string for view in placeholder
 * @param {function} props.recSearch - function to generate search domain based on query and current record
 * @param {Array} props.options - list of options if model is not provided
 * @param {Array} props.attrs - list of fields to include in the option objects if model is provided
 * @param {object} props.record - current record object
 * @param {function} props.onChange - callback function called when field value changes
 * @param {any} props.value - field value
 * @return {Component} - selection component
 */
function Selection(props) {
  const {
    name,
    field,
    data,
    recName,
    translate,
    readOnly,
    options,
    placeholder,
    record,
    label,
    onChange,
    model,
    colSpan,
  } = props;

  function onRemove() {
    handleChange({ id: null, name: '' });
  }

  function handleChange(value) {
    // if (!value) return;
    data.value = value;
    if (onChange) {
      onChange(name, value);
    }
  }

  let color = "";
  let styleDisabled = "";
  let chevronIconBg = "bg-white";
  if (readOnly) {
    color = colors.readOnly;
    chevronIconBg = colors.readOnly;
    styleDisabled = "pointer-events-none";
  }

  let nameRec = "";
  if (data?.value && typeof data.value === "object") {
    nameRec = data.value.name ?? data.value.rec_name;
  } else if (record) {
    let _name = name;
    if (record && typeof record[name] === "number") {
      _name = `${name}.`;
    }
    if (record[_name]) {
      nameRec = record[_name].name || record[_name].rec_name || "";
    }
  }

  const val = strTranslate(translate, model, nameRec);
  console.log("options....", options);
  return (
    <FormField
      label={label}
      name={name}
      required={field?.required}
      colspan={colSpan}
      model={model}
    >
      <Combobox className="flex" as="div" value={val} onChange={handleChange}>
        <div className={cn("relative w-full", theme.input.height)}>
          <ComboboxInput
            disabled={readOnly}
            readOnly={readOnly}
            className={cn(style, color, theme.textColor, theme.border)}
            placeholder={placeholder}
          />
          <ComboboxButton
            disabled={readOnly}
            readOnly={readOnly}
            className={cn(
              "absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none",
              styleDisabled,
            )}
          >
            {!data.value?.id ? (
              <ChevronDownIcon
                className={cn(
                  "fill-current",
                  chevronIconBg,
                  theme.textColor,
                  theme.input.iconSize,
                )}
                aria-hidden="true"
              />
            ) : (
              !readOnly && (
                <TrashIcon
                  onClick={onRemove}
                  className={cn(
                    "my-auto active:text-rose-600 cursor-pointer active:scale-90 fill-current",
                    theme.textColor,
                    theme.input.iconSize,
                  )}
                  aria-hidden="true"
                />
              )
            )}
          </ComboboxButton>

          {options?.length > 0 && (
            <ComboboxOptions className="z-99 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-neutral-200 ring-opacity-5 focus:outline-none sm:text-sm">
              {options.map((option) => (
                <ComboboxOption
                  key={option.id}
                  value={option}
                  className="relative cursor-default select-none py-1 pl-3 pr-9 text-gray-900 data-focus:bg-blue-400 data-focus:text-white"
                >
                  <span className="block h-5 truncate">
                    {translate ? (
                      <FM id={`${model}.${option.name}`} />
                    ) : (
                      option.name
                    )}
                  </span>
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          )}
        </div>
      </Combobox>
    </FormField>
  );
}

export default Selection;
